import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'
import StrategyInfoTooltip from './StrategyInfoTooltip'

function DteSimView({ onBack, state, onNavigate }) {
  const locked = !state?.tier2Unlocked;
  const reqCards = [
    { icon: '📊', title: 'Paper Trading', text: '30+ paper trades logged with consistent position sizing and risk management' },
    { icon: '🎯', title: 'Win Rate', text: 'Consistent results on CSP and Covered Call trades demonstrating strategy mastery' },
    { icon: '🛡', title: 'Risk Discipline', text: 'No trades exceeding 1-2% risk per trade. Total portfolio risk held under 5%. Daily max loss rule followed consistently' },
    { icon: '🧠', title: 'Emotional Readiness', text: 'Demonstrated ability to follow rules under pressure. Inner Work practice established' },
  ];

  return (
    <div style={{ padding: '28px 40px', maxWidth: '860px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
      <button style={{ ...btnGhost, marginBottom: '24px', fontSize: '12px' }} onClick={onBack}>← Practice</button>
      <div style={{ ...eyebrow, marginBottom: '6px' }}>TIER 2 — INTERMEDIATE</div>
      <h1 style={{ ...serif, fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 24px' }}>0DTE Spread Simulator</h1>

      {locked ? (
        <div style={{ background: V.bg2, border: '1px solid rgba(201,168,76,0.2)', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', color: V.gold, marginBottom: '16px' }}>🔒</div>
          <h2 style={{ ...serif, fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 8px' }}>0DTE Spreads</h2>
          <p style={{ ...serif, fontSize: '18px', fontStyle: 'italic', color: V.muted, margin: '0 0 24px', lineHeight: 1.5 }}>This is not where you start. This is where you arrive.</p>
          <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.8)', fontFamily: "'Jost',sans-serif", lineHeight: 1.9, maxWidth: '560px', margin: '0 auto 32px', textAlign: 'left', whiteSpace: 'pre-line' }}>
            {`0DTE trading is one of the most exciting and demanding strategies available to options traders. Same-day premium. Fast decisions. High gamma. Active management required during market hours.\n\nThis is not where you begin your journey. This is where you arrive after you have mastered the foundation — after selling puts and calls has become second nature, after you have logged real paper trades and learned how your emotions respond to positions moving against you.\n\n0DTE requires everything the foundation teaches — risk management, emotional regulation, spread mechanics, gamma awareness — all applied in real time under market hours pressure.\n\nWhen your mentor determines you are ready — when your foundation trading shows the discipline, consistency, and emotional regulation that 0DTE demands — this strategy will unlock.`}
          </p>
          <div style={{ height: '0.5px', background: 'rgba(201,168,76,0.15)', marginBottom: '24px' }} />
          <h3 style={{ ...serif, fontSize: '20px', fontWeight: 400, color: V.gold, margin: '0 0 16px' }}>What You Are Building Toward</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px', textAlign: 'left' }}>
            {reqCards.map(({ icon, title, text }) => (
              <div key={title} style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.15)`, borderRadius: '8px', padding: '16px' }}>
                <div style={{ fontSize: '20px', marginBottom: '6px' }}>{icon}</div>
                <div style={{ fontSize: '13px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 500, marginBottom: '4px' }}>{title}</div>
                <div style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.6 }}>{text}</div>
              </div>
            ))}
          </div>
          <div style={{ height: '0.5px', background: 'rgba(201,168,76,0.15)', marginBottom: '24px' }} />
          <div style={{ borderLeft: `3px solid ${V.gold}`, background: 'rgba(201,168,76,0.04)', borderRadius: '0 8px 8px 0', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
            <p style={{ ...serif, fontSize: '16px', fontStyle: 'italic', color: V.gold, lineHeight: 1.7, margin: 0 }}>
              "In trading patience is not just a virtue — it is a strategy. The traders who master the foundation before advancing are the ones who survive long enough to thrive."
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{ ...btnGold, padding: '12px 22px', fontSize: '13px' }} onClick={() => onNavigate && onNavigate('strategycharts')}>Review Foundation Strategies →</button>
          </div>
          <p style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginTop: '16px' }}>
            Your mentor will unlock this when your foundation trading is ready.
          </p>
        </div>
      ) : (
        <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', padding: '4px 12px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid ${V.border}`, borderRadius: '20px', fontSize: '10px', color: V.gold, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '20px' }}>Coming Soon</div>
          <h2 style={{ ...serif, fontSize: '24px', fontWeight: 400, color: V.gold, margin: '0 0 16px' }}>Full Simulator Coming Soon</h2>
          <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, maxWidth: '520px', margin: '0 auto 24px' }}>
            The complete 0DTE simulator is being built specifically for Tier 2 students. It will include a live SPX chart, positions panel, account tracker, decision points, and real scenario practice with Inner Work integration.
            <br /><br />
            In the meantime use the Strategy Charts to study the 0DTE mechanics.
          </p>
          <button style={{ ...btnGold, padding: '12px 22px', fontSize: '13px' }} onClick={() => onNavigate && onNavigate('strategycharts')}>
            Study 0DTE in Strategy Charts →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CALCULATOR SHARED STYLES ───────────────────────────────────
const cInp = { padding: '10px 12px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.text, fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Jost',sans-serif", marginTop: '4px' };
const cLbl = { fontSize: '11px', color: V.muted, letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 500, fontFamily: "'Jost',sans-serif" };
const cCard = { background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '20px', marginBottom: '16px' };
const cQuote = (q, a) => (
  <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' }}>
    <p style={{ ...serif, fontSize: '15px', fontStyle: 'italic', color: V.muted, lineHeight: 1.7, margin: '0 0 6px' }}>"{q}"</p>
    <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>— {a}</div>
  </div>
);
const cResultRow = ({ label, value, color = V.gold, small = false }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '8px 0', borderBottom: `0.5px solid rgba(201,168,76,0.08)` }}>
    <span style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{label}</span>
    <span style={{ ...mono, fontSize: small ? '14px' : '18px', color, fontWeight: 500 }}>{value}</span>
  </div>
);

function CalcHeader({ title, subtitle, onBack }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <button style={{ ...btnGhost, marginBottom: '20px', fontSize: '12px' }} onClick={onBack}>← Calculators</button>
      <div style={{ ...eyebrow, marginBottom: '6px' }}>Calculator</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, color: V.gold, margin: '0 0 4px' }}>{title}</h2>
      <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{subtitle}</p>
    </div>
  );
}

// ─── CALC 1: POSITION SIZE ──────────────────────────────────────
function CalcPositionView({ state, onBack }) {
  const [mode, setMode] = useState('find'); // 'find' | 'check'

  // Mode A — Find contract size
  const [acct, setAcct] = useState(String(state.currentBalance || ''));
  const [riskPct, setRiskPct] = useState('1');
  const [premiumA, setPremiumA] = useState('');
  const [strikeA, setStrikeA] = useState('');
  const [strike2A, setStrike2A] = useState('');
  const [strategyA, setStrategyA] = useState('CSP');

  // Mode B — Check my risk
  const [acctB, setAcctB] = useState(String(state.currentBalance || ''));
  const [contractsB, setContractsB] = useState('1');
  const [premiumB, setPremiumB] = useState('');
  const [strikeB, setStrikeB] = useState('');
  const [strike2B, setStrike2B] = useState('');
  const [strategyB, setStrategyB] = useState('CSP');

  // ── Mode A calculations ──
  const aA = parseFloat(acct) || 0;
  const r  = parseFloat(riskPct) || 1;
  const pA = parseFloat(premiumA) || 0;
  const sA = parseFloat(strikeA) || 0;
  const s2A = parseFloat(strike2A) || 0;
  const isSpreadA = ['Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].includes(strategyA);

  let maxLossA = 0;
  if (isSpreadA && sA > 0 && s2A > 0) {
    maxLossA = Math.max(0, (Math.abs(sA - s2A) - pA) * 100);
  } else if (pA > 0) {
    maxLossA = pA * 100 * 2;
  }
  const maxContracts  = maxLossA > 0 ? Math.floor((aA * r / 100) / maxLossA) : 0;
  const totalCreditA  = pA * Math.max(maxContracts, 1) * 100;
  const riskPctActualA = aA > 0 && maxLossA > 0 ? (maxLossA * Math.max(maxContracts, 1) / aA * 100) : 0;
  const riskColorA = riskPctActualA < 1 ? V.sage : riskPctActualA < 2 ? V.gold : V.rose;

  // ── Mode B calculations ──
  const aB  = parseFloat(acctB) || 0;
  const cB  = parseInt(contractsB) || 1;
  const pB  = parseFloat(premiumB) || 0;
  const sB  = parseFloat(strikeB) || 0;
  const s2B = parseFloat(strike2B) || 0;
  const isSpreadB = ['Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].includes(strategyB);

  const totalCreditB = pB * cB * 100;
  let maxLossB = 0;
  if (isSpreadB && sB > 0 && s2B > 0) {
    maxLossB = Math.max(0, (Math.abs(sB - s2B) - pB) * cB * 100);
  } else if (pB > 0) {
    maxLossB = pB * cB * 100 * 2;
  }
  const riskPctB  = aB > 0 && maxLossB > 0 ? (maxLossB / aB * 100) : 0;
  const riskColorB = riskPctB < 1 ? V.sage : riskPctB < 2 ? V.gold : V.rose;

  const pillBtn = (active) => ({
    padding: '9px 20px',
    borderRadius: '20px',
    border: `1px solid ${active ? V.gold : V.border}`,
    background: active ? 'rgba(201,168,76,0.15)' : 'transparent',
    color: active ? V.goldL : V.muted,
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: "'Jost',sans-serif",
    fontWeight: active ? 500 : 400,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  });

  const guidelinesCard = (
    <div style={{ background: 'rgba(201,168,76,0.08)', borderLeft: '3px solid rgba(201,168,76,0.25)', borderRadius: '8px', padding: '14px 16px', marginBottom: '24px' }}>
      <div style={{ fontSize: '12px', fontFamily: "'Jost',sans-serif", color: V.gold, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 500, marginBottom: '12px' }}>Suggested Risk Guidelines</div>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
          <div style={{ fontSize: '11px', fontFamily: "'Jost',sans-serif", color: V.muted, marginBottom: '6px' }}>Per Trade</div>
          {[['🟢', 'Under 1%', '— Ideal'], ['🟡', '1% to 2%', '— Acceptable'], ['🔴', 'Over 2%', '— Reconsider sizing']].map(([dot, pct, label]) => (
            <div key={pct} style={{ fontSize: '12px', fontFamily: "'Jost',sans-serif", color: V.muted, lineHeight: 1.8 }}>{dot} <span style={{ color: V.text }}>{pct}</span>{label}</div>
          ))}
        </div>
        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
          <div style={{ fontSize: '11px', fontFamily: "'Jost',sans-serif", color: V.muted, marginBottom: '6px' }}>Total Portfolio</div>
          {[['🟢', 'Under 2%', '— Protected'], ['🟡', '2% to 5%', '— Monitor closely'], ['🔴', 'Over 5%', '— Reduce exposure']].map(([dot, pct, label]) => (
            <div key={pct} style={{ fontSize: '12px', fontFamily: "'Jost',sans-serif", color: V.muted, lineHeight: 1.8 }}>{dot} <span style={{ color: V.text }}>{pct}</span>{label}</div>
          ))}
        </div>
      </div>
      <p style={{ fontSize: '11px', fontFamily: "'Jost',sans-serif", color: V.muted, fontStyle: 'italic', lineHeight: 1.7, margin: '0 0 10px' }}>
        These are suggested guidelines, not rules. Your risk tolerance is personal. The goal is to stay in the game long enough for your edge to work.
      </p>
      <p style={{ ...serif, fontSize: '13px', color: V.muted, fontStyle: 'italic', textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
        "The first rule is not to lose. The second rule is not to forget the first rule." — Warren Buffett
      </p>
    </div>
  );

  return (
    <div style={{ padding: '28px 24px', maxWidth: '640px' }}>
      <CalcHeader title="Position Size Calculator" subtitle="Understand your risk before you trade." onBack={onBack} />

      {guidelinesCard}

      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <button style={pillBtn(mode === 'find')}  onClick={() => setMode('find')}>Find my contract size</button>
        <button style={pillBtn(mode === 'check')} onClick={() => setMode('check')}>Check my risk</button>
      </div>

      {/* ── MODE A ── */}
      {mode === 'find' && (
        <>
          <div style={cCard}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={cLbl}>Options Buying Power ($)</div>
                <input style={cInp} type="number" placeholder="e.g. 25000" value={acct} onChange={e => setAcct(e.target.value)} />
                <div style={{ fontSize: '11px', fontStyle: 'italic', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '4px' }}>From TOS: Monitor → Balances → Options Buying Power</div>
              </div>
              <div><div style={cLbl}>Max Risk Per Trade (%)</div><input style={cInp} type="number" step="0.5" placeholder="1" value={riskPct} onChange={e => setRiskPct(e.target.value)} /></div>
              <div>
                <div style={cLbl}>Strategy<StrategyInfoTooltip /></div>
                <select style={{ ...cInp }} value={strategyA} onChange={e => setStrategyA(e.target.value)}>
                  {['CSP', 'CC', 'Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              {isSpreadA && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}><div style={cLbl}>Short Strike ($)</div><input style={cInp} type="number" placeholder="e.g. 420" value={strikeA} onChange={e => setStrikeA(e.target.value)} /></div>
                  <div style={{ flex: 1 }}><div style={cLbl}>Long Strike ($)</div><input style={cInp} type="number" placeholder="e.g. 415" value={strike2A} onChange={e => setStrike2A(e.target.value)} /></div>
                </div>
              )}
              <div><div style={cLbl}>Premium Received ($ per share)</div><input style={cInp} type="number" step="0.01" placeholder="e.g. 1.50" value={premiumA} onChange={e => setPremiumA(e.target.value)} /></div>
            </div>
          </div>
          <div style={{ ...cCard, borderColor: maxContracts > 0 ? 'rgba(201,168,76,0.35)' : V.border }}>
            <div style={{ ...eyebrow, marginBottom: '16px' }}>Results</div>
            {cResultRow({ label: 'Recommended Contracts', value: maxContracts > 0 ? maxContracts : '—', color: V.gold })}
            {cResultRow({ label: 'Max Loss at This Size', value: maxLossA > 0 ? `$${(maxLossA * Math.max(maxContracts, 1)).toFixed(0)}` : '—', color: riskColorA })}
            {cResultRow({ label: '% of Account at Risk', value: riskPctActualA > 0 ? `${riskPctActualA.toFixed(2)}%` : '—', color: riskColorA })}
            {cResultRow({ label: 'Total Credit If Filled', value: totalCreditA > 0 ? `$${totalCreditA.toFixed(0)}` : '—', color: V.sage })}
          </div>
          <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>
            This calculator uses the 2× rule as your practical maximum loss for CSP and CC trades. If premium doubles against you — close the trade. This keeps your real risk manageable.
          </p>
        </>
      )}

      {/* ── MODE B ── */}
      {mode === 'check' && (
        <>
          <div style={cCard}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={cLbl}>Options Buying Power ($)</div>
                <input style={cInp} type="number" placeholder="e.g. 25000" value={acctB} onChange={e => setAcctB(e.target.value)} />
                <div style={{ fontSize: '11px', fontStyle: 'italic', color: V.dim, fontFamily: "'Jost',sans-serif", marginTop: '4px' }}>From TOS: Monitor → Balances → Options Buying Power</div>
              </div>
              <div>
                <div style={cLbl}>Strategy<StrategyInfoTooltip /></div>
                <select style={{ ...cInp }} value={strategyB} onChange={e => setStrategyB(e.target.value)}>
                  {['CSP', 'CC', 'Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}><div style={cLbl}>Contracts</div><input style={cInp} type="number" min="1" placeholder="e.g. 10" value={contractsB} onChange={e => setContractsB(e.target.value)} /></div>
                <div style={{ flex: 1 }}><div style={cLbl}>Premium ($ per share)</div><input style={cInp} type="number" step="0.01" placeholder="e.g. 0.60" value={premiumB} onChange={e => setPremiumB(e.target.value)} /></div>
              </div>
              {isSpreadB ? (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}><div style={cLbl}>Short Strike ($)</div><input style={cInp} type="number" placeholder="e.g. 5200" value={strikeB} onChange={e => setStrikeB(e.target.value)} /></div>
                  <div style={{ flex: 1 }}><div style={cLbl}>Long Strike ($)</div><input style={cInp} type="number" placeholder="e.g. 5190" value={strike2B} onChange={e => setStrike2B(e.target.value)} /></div>
                </div>
              ) : (
                <div><div style={cLbl}>Strike Price ($)</div><input style={cInp} type="number" placeholder="e.g. 5200" value={strikeB} onChange={e => setStrikeB(e.target.value)} /></div>
              )}
            </div>
          </div>
          <div style={{ ...cCard, borderColor: totalCreditB > 0 ? 'rgba(201,168,76,0.35)' : V.border }}>
            <div style={{ ...eyebrow, marginBottom: '16px' }}>Results</div>
            {cResultRow({ label: 'Total Credit Collected', value: totalCreditB > 0 ? `$${totalCreditB.toFixed(0)}` : '—', color: V.sage })}
            {cResultRow({ label: 'Practical Max Loss (2× rule)', value: maxLossB > 0 ? `$${maxLossB.toFixed(0)}` : '—', color: riskColorB })}
            {cResultRow({ label: '% of Account at Risk (this trade)', value: riskPctB > 0 ? `${riskPctB.toFixed(2)}%` : '—', color: riskColorB })}
            {riskPctB > 0 && (
              <div style={{ marginTop: '14px', padding: '10px 14px', background: `${riskColorB}12`, border: `0.5px solid ${riskColorB}44`, borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', fontFamily: "'Jost',sans-serif", color: riskColorB, lineHeight: 1.7 }}>
                  {riskPctB < 1
                    ? '✓ Within the ideal 1% per-trade risk zone. Well sized.'
                    : riskPctB < 2
                    ? '◆ Between 1–2% per trade. Acceptable — stay alert.'
                    : `⚠ This trade risks ${riskPctB.toFixed(1)}% of your account — above the 2% guideline. Consider reducing to ${Math.max(1, Math.floor(cB * 2 / riskPctB))} contracts.`}
                </div>
              </div>
            )}
          </div>
          <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>
            For spreads the max loss is (spread width − premium) × contracts × 100. For CSP and CC the 2× rule is used as practical max loss.
          </p>
        </>
      )}
    </div>
  );
}

// ─── CALC 2: OPTIONS PROFIT ─────────────────────────────────────
function CalcOptionsProfitView({ onBack }) {
  const [strategy, setStrategy] = useState('CSP');
  const [premium, setPremium] = useState('');
  const [contracts, setContracts] = useState('1');
  const [spreadWidth, setSpreadWidth] = useState('');
  const [dte, setDte] = useState('30');
  const [strike, setStrike] = useState('');

  const p = parseFloat(premium) || 0;
  const c = parseInt(contracts) || 1;
  const sw = parseFloat(spreadWidth) || 0;
  const d = parseInt(dte) || 30;
  const s = parseFloat(strike) || 0;
  const isSpread = ['Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].includes(strategy);

  const totalCredit = p * c * 100;
  const maxProfit = totalCredit;
  const maxLoss = isSpread ? Math.max(0, (sw - p) * c * 100) : p * c * 100 * 2;
  const halfProfit = totalCredit / 2;
  const annualized = maxLoss > 0 && d > 0 ? (maxProfit / maxLoss) * (365 / d) * 100 : 0;
  const breakeven = strategy === 'CC' ? s + p : s > 0 ? s - p : 0;

  return (
    <div style={{ padding: '28px 24px', maxWidth: '640px' }}>
      <CalcHeader title="Options Profit Calculator" subtitle="What will I make on this trade?" onBack={onBack} />
      {cQuote('The goal of a successful trader is to make the best trades. Money is secondary.', 'Alexander Elder')}

      <div style={cCard}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <div style={cLbl}>Strategy<StrategyInfoTooltip /></div>
            <select style={{ ...cInp }} value={strategy} onChange={e => setStrategy(e.target.value)}>
              {['CSP', 'CC', 'Bull Put Spread', 'Bear Call Spread', '0DTE Spread'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}><div style={cLbl}>Premium ($ per share)</div><input style={cInp} type="number" step="0.01" placeholder="1.50" value={premium} onChange={e => setPremium(e.target.value)} /></div>
            <div style={{ flex: 1 }}><div style={cLbl}>Contracts</div><input style={cInp} type="number" min="1" placeholder="1" value={contracts} onChange={e => setContracts(e.target.value)} /></div>
          </div>
          {isSpread && <div><div style={cLbl}>Spread Width ($)</div><input style={cInp} type="number" placeholder="e.g. 5" value={spreadWidth} onChange={e => setSpreadWidth(e.target.value)} /></div>}
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}><div style={cLbl}>Short Strike ($)</div><input style={cInp} type="number" placeholder="e.g. 420" value={strike} onChange={e => setStrike(e.target.value)} /></div>
            <div style={{ flex: 1 }}><div style={cLbl}>Days to Expiration</div><input style={cInp} type="number" placeholder="30" value={dte} onChange={e => setDte(e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div style={{ ...cCard, borderColor: maxProfit > 0 ? 'rgba(201,168,76,0.35)' : V.border }}>
        <div style={{ ...eyebrow, marginBottom: '16px' }}>Results</div>
        {cResultRow({ label: 'Total Credit Collected', value: totalCredit > 0 ? `$${totalCredit.toFixed(0)}` : '—', color: V.sage })}
        {cResultRow({ label: 'Max Profit (expires worthless)', value: maxProfit > 0 ? `$${maxProfit.toFixed(0)}` : '—', color: V.sage })}
        {cResultRow({ label: 'Max Loss', value: maxLoss > 0 ? `$${maxLoss.toFixed(0)}` : '—', color: V.rose })}
        {cResultRow({ label: '50% Profit Target', value: halfProfit > 0 ? `$${halfProfit.toFixed(0)}` : '—', color: V.gold, small: true })}
        {cResultRow({ label: 'Breakeven Price', value: breakeven > 0 ? `$${breakeven.toFixed(2)}` : '—', color: V.muted, small: true })}
        {cResultRow({ label: 'Est. Annualized Return', value: annualized > 0 ? `${annualized.toFixed(0)}%` : '—', color: V.gold, small: true })}
      </div>
      <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7 }}>
        Close at 50% profit to lock in half your max gain while eliminating late-trade risk. Annualized return assumes this trade repeats every cycle.
      </p>
    </div>
  );
}

// ─── CALC 3: BREAKEVEN ──────────────────────────────────────────
function CalcBreakevenView({ onBack }) {
  const [strategy, setStrategy] = useState('CSP');
  const [strike, setStrike] = useState('');
  const [strike2, setStrike2] = useState('');
  const [premium, setPremium] = useState('');
  const [contracts, setContracts] = useState('1');

  const s = parseFloat(strike) || 0;
  const s2 = parseFloat(strike2) || 0;
  const p = parseFloat(premium) || 0;
  const c = parseInt(contracts) || 1;
  const isSpread = ['Bull Put Spread', 'Bear Call Spread'].includes(strategy);

  const breakeven = strategy === 'CC' || strategy === 'Bear Call Spread' ? s + p : s - p;
  const dropPct = s > 0 ? Math.abs(s - breakeven) / s * 100 : 0;

  const lineMin = Math.min(s, s2 || s, breakeven) * 0.95;
  const lineMax = Math.max(s, s2 || s, breakeven) * 1.05;
  const pct = (v) => `${((v - lineMin) / (lineMax - lineMin) * 100).toFixed(1)}%`;

  return (
    <div style={{ padding: '28px 24px', maxWidth: '640px' }}>
      <CalcHeader title="Break Even Calculator" subtitle="Where does my trade start losing?" onBack={onBack} />
      {cQuote('Know your exit before you enter.', 'Trading Wisdom')}

      <div style={cCard}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <div style={cLbl}>Strategy<StrategyInfoTooltip /></div>
            <select style={{ ...cInp }} value={strategy} onChange={e => setStrategy(e.target.value)}>
              {['CSP', 'CC', 'Bull Put Spread', 'Bear Call Spread'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}><div style={cLbl}>Short Strike ($)</div><input style={cInp} type="number" placeholder="420" value={strike} onChange={e => setStrike(e.target.value)} /></div>
            {isSpread && <div style={{ flex: 1 }}><div style={cLbl}>Long Strike ($)</div><input style={cInp} type="number" placeholder="415" value={strike2} onChange={e => setStrike2(e.target.value)} /></div>}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}><div style={cLbl}>Premium ($ per share)</div><input style={cInp} type="number" step="0.01" placeholder="1.50" value={premium} onChange={e => setPremium(e.target.value)} /></div>
            <div style={{ flex: 1 }}><div style={cLbl}>Contracts</div><input style={cInp} type="number" min="1" placeholder="1" value={contracts} onChange={e => setContracts(e.target.value)} /></div>
          </div>
        </div>
      </div>

      {s > 0 && p > 0 && (
        <>
          <div style={{ ...cCard, borderColor: 'rgba(201,168,76,0.35)' }}>
            <div style={{ ...eyebrow, marginBottom: '16px' }}>Results</div>
            {cResultRow({ label: 'Breakeven Price', value: `$${breakeven.toFixed(2)}`, color: V.gold })}
            {cResultRow({ label: 'Short Strike', value: `$${s.toFixed(2)}`, color: V.muted, small: true })}
            {isSpread && cResultRow({ label: 'Long Strike', value: `$${s2.toFixed(2)}`, color: V.dim, small: true })}
            {cResultRow({ label: 'Distance to Breakeven', value: `$${Math.abs(s - breakeven).toFixed(2)} (${dropPct.toFixed(1)}%)`, color: V.sage, small: true })}
            <div style={{ marginTop: '16px', fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7 }}>
              {strategy === 'CSP' || strategy === 'Bull Put Spread'
                ? `Your breakeven is $${Math.abs(s - breakeven).toFixed(2)} below your strike — the stock can drop ${dropPct.toFixed(1)}% before you start losing.`
                : `Your breakeven is $${Math.abs(s - breakeven).toFixed(2)} above your strike — the stock can rise ${dropPct.toFixed(1)}% before you start losing.`}
            </div>
          </div>

          {lineMax > lineMin && (
            <div style={{ ...cCard }}>
              <div style={{ ...eyebrow, marginBottom: '12px' }}>Price Map</div>
              <div style={{ position: 'relative', height: '48px', background: 'rgba(240,237,230,0.04)', borderRadius: '6px', marginBottom: '8px', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: (strategy === 'CSP' || strategy === 'Bull Put Spread') ? `linear-gradient(to right, rgba(196,122,122,0.3) 0%, rgba(196,122,122,0.3) ${pct(breakeven)}, rgba(122,171,138,0.3) ${pct(breakeven)}, rgba(122,171,138,0.3) 100%)` : `linear-gradient(to right, rgba(122,171,138,0.3) 0%, rgba(122,171,138,0.3) ${pct(breakeven)}, rgba(196,122,122,0.3) ${pct(breakeven)}, rgba(196,122,122,0.3) 100%)` }} />
                {s > lineMin && s < lineMax && <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(s), width: '2px', background: V.gold }} />}
                {breakeven > lineMin && breakeven < lineMax && <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(breakeven), width: '2px', background: 'rgba(240,237,230,0.8)' }} />}
                {isSpread && s2 > lineMin && s2 < lineMax && <div style={{ position: 'absolute', top: 0, bottom: 0, left: pct(s2), width: '2px', background: V.sky }} />}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>
                <span style={{ color: V.gold }}>◆ Strike ${s}</span>
                <span style={{ color: 'rgba(240,237,230,0.7)' }}>| Breakeven ${breakeven.toFixed(2)}</span>
                {isSpread && <span style={{ color: V.sky }}>Long ${s2}</span>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── CALC 4: RISK / REWARD ──────────────────────────────────────
function CalcRRView({ onBack }) {
  const [maxProfit, setMaxProfit] = useState('');
  const [maxLoss, setMaxLoss] = useState('');
  const [pop, setPop] = useState('');

  const mp = parseFloat(maxProfit) || 0;
  const ml = parseFloat(maxLoss) || 0;
  const probPct = parseFloat(pop) || 0;
  const prob = probPct / 100;

  const ratio = mp > 0 ? (ml / mp) : 0;
  const ev = probPct > 0 ? (prob * mp) - ((1 - prob) * ml) : null;

  let verdict = null;
  let verdictColor = V.muted;
  if (mp > 0 && ml > 0) {
    if (ratio < 3 && (ev === null || ev > 0)) { verdict = 'Favorable risk/reward. Worth considering if the setup is strong.'; verdictColor = V.sage; }
    else if (ratio <= 5) { verdict = 'Moderate risk/reward. Make sure your probability of profit justifies this.'; verdictColor = V.gold; }
    else { verdict = 'High risk relative to reward. Consider a different strike or strategy.'; verdictColor = V.rose; }
  }

  return (
    <div style={{ padding: '28px 24px', maxWidth: '640px' }}>
      <CalcHeader title="Risk / Reward Calculator" subtitle="Is this trade worth taking?" onBack={onBack} />
      {cQuote("It's not about being right — it's about making money when you're right.", 'Mark Douglas')}

      <div style={cCard}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}><div style={cLbl}>Max Profit ($)</div><input style={cInp} type="number" placeholder="150" value={maxProfit} onChange={e => setMaxProfit(e.target.value)} /></div>
            <div style={{ flex: 1 }}><div style={cLbl}>Max Loss ($)</div><input style={cInp} type="number" placeholder="850" value={maxLoss} onChange={e => setMaxLoss(e.target.value)} /></div>
          </div>
          <div><div style={cLbl}>Probability of Profit (%) — optional</div><input style={cInp} type="number" placeholder="e.g. 70 (roughly 1 − delta × 100)" value={pop} onChange={e => setPop(e.target.value)} /></div>
        </div>
      </div>

      {mp > 0 && ml > 0 && (
        <div style={{ ...cCard, borderColor: 'rgba(201,168,76,0.35)' }}>
          <div style={{ ...eyebrow, marginBottom: '16px' }}>Results</div>
          <div style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '16px' }}>
            You are risking <span style={{ color: V.rose, fontWeight: 500 }}>${ml.toFixed(0)}</span> to make <span style={{ color: V.sage, fontWeight: 500 }}>${mp.toFixed(0)}</span>
          </div>
          {cResultRow({ label: 'Risk/Reward Ratio', value: `${ratio.toFixed(1)}:1`, color: ratio < 3 ? V.sage : ratio < 5 ? V.gold : V.rose })}
          {ev !== null && cResultRow({ label: 'Expected Value', value: `$${ev.toFixed(0)}`, color: ev >= 0 ? V.sage : V.rose })}
          {verdict && (
            <div style={{ marginTop: '16px', padding: '12px 16px', background: `${verdictColor}12`, border: `0.5px solid ${verdictColor}44`, borderRadius: '8px' }}>
              <div style={{ fontSize: '13px', color: verdictColor, fontFamily: "'Jost',sans-serif", lineHeight: 1.7 }}>{verdict}</div>
            </div>
          )}
        </div>
      )}
      <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, marginTop: '12px' }}>
        Options selling typically involves asymmetric risk/reward — high probability of small wins offset by occasional larger losses. This is normal and manageable with proper position sizing.
      </p>
    </div>
  );
}

// ─── CALC 5: COMPOUND GROWTH ────────────────────────────────────
function CalcCompoundView({ state, onBack }) {
  const [startingBalance, setStartingBalance] = useState(10000);
  const [annualReturn, setAnnualReturn] = useState(25);
  const [years, setYears] = useState(10);
  const [monthlyContribution, setMonthlyContribution] = useState(0);

  const fmt = (n) => {
    if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    return `$${Math.round(n).toLocaleString()}`;
  };

  const yearlyData = useMemo(() => {
    const data = [];
    let balance = startingBalance;
    for (let y = 1; y <= years; y++) {
      const startBalance = balance;
      const yearGrowth = balance * (annualReturn / 100);
      const contributions = monthlyContribution * 12;
      balance = balance + yearGrowth + contributions;
      data.push({ year: y, start: startBalance, growth: yearGrowth + contributions, end: balance });
    }
    return data;
  }, [startingBalance, annualReturn, years, monthlyContribution]);

  const finalBalance = yearlyData[yearlyData.length - 1]?.end || startingBalance;
  const totalContributed = monthlyContribution * 12 * years;
  const totalGrowth = finalBalance - startingBalance - totalContributed;
  const totalReturnPct = Math.round((finalBalance / startingBalance - 1) * 100);

  // SVG line chart
  const svgW = 600, svgH = 160;
  const padL = 56, padR = 12, padT = 12, padB = 28;
  const maxBal = Math.max(...yearlyData.map(d => d.end), startingBalance);
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;
  const toX = (i) => padL + (yearlyData.length > 1 ? (i / (yearlyData.length - 1)) * chartW : chartW / 2);
  const toY = (val) => padT + chartH - (val / maxBal) * chartH;
  const points = yearlyData.map((d, i) => `${toX(i)},${toY(d.end)}`).join(' ');
  const areaPoints = `${padL},${padT + chartH} ${points} ${toX(yearlyData.length - 1)},${padT + chartH}`;
  const yLabels = [0, 0.25, 0.5, 0.75, 1].map(f => ({ val: maxBal * f, y: padT + chartH - f * chartH }));
  const xStep = Math.max(1, Math.ceil(years / 5));

  const sliderRow = (label, value, display, min, max, step, setter) => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={cLbl}>{label}</span>
        <span style={{ fontSize: '13px', color: V.gold, fontFamily: "'Jost',sans-serif", fontWeight: 600 }}>{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => setter(Number(e.target.value))}
        style={{ width: '100%', accentColor: V.gold }} />
    </div>
  );

  return (
    <div style={{ padding: '28px 24px', maxWidth: '700px' }}>
      <CalcHeader title="Compound Growth Calculator" subtitle="What does consistent premium selling build over time?" onBack={onBack} />
      {cQuote('Compound interest is the eighth wonder of the world. He who understands it, earns it.', 'Albert Einstein')}

      {/* INPUTS */}
      <div style={cCard}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {sliderRow('Starting Balance', startingBalance, `$${startingBalance.toLocaleString()}`, 1000, 500000, 1000, setStartingBalance)}
          {sliderRow('Annual Return', annualReturn, `${annualReturn}% per year`, 0, 100, 1, setAnnualReturn)}
          {sliderRow('Years', years, `${years} year${years !== 1 ? 's' : ''}`, 1, 30, 1, setYears)}
          {sliderRow('Monthly Contribution', monthlyContribution, `$${monthlyContribution.toLocaleString()} per month`, 0, 5000, 100, setMonthlyContribution)}
        </div>
      </div>

      {/* SVG GROWTH CHART */}
      <div style={{ ...cCard, padding: '16px' }}>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ display: 'block' }}>
          {yLabels.map(({ y, val }, i) => (
            <g key={i}>
              <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="rgba(201,168,76,0.1)" strokeWidth="0.5" />
              <text x={padL - 4} y={y + 3} fontSize="9" fill="rgba(240,237,230,0.35)" textAnchor="end" fontFamily="Jost,sans-serif">
                {val >= 1e6 ? `$${(val / 1e6).toFixed(1)}M` : val >= 1e3 ? `$${(val / 1e3).toFixed(0)}k` : `$${Math.round(val)}`}
              </text>
            </g>
          ))}
          {yearlyData.length > 1 && <polygon points={areaPoints} fill="rgba(201,168,76,0.07)" />}
          {yearlyData.length > 1 && <polyline points={points} fill="none" stroke={V.gold} strokeWidth="2" strokeLinejoin="round" />}
          {yearlyData.map((d, i) => (d.year % xStep === 0 || d.year === 1) && (
            <text key={i} x={toX(i)} y={svgH - 4} fontSize="9" fill="rgba(240,237,230,0.3)" textAnchor="middle" fontFamily="Jost,sans-serif">
              Yr {d.year}
            </text>
          ))}
        </svg>
      </div>

      {/* SUMMARY CARDS */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ flex: '1 1 160px', background: 'rgba(122,171,138,0.08)', border: '0.5px solid rgba(122,171,138,0.3)', borderRadius: '10px', padding: '18px' }}>
          <div style={{ fontSize: '10px', color: V.sage, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '6px' }}>Final Balance</div>
          <div style={{ ...mono, fontSize: '20px', color: V.sage, marginBottom: '4px' }}>{fmt(finalBalance)}</div>
          <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>After {years} year{years !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ flex: '1 1 160px', background: 'rgba(201,168,76,0.07)', border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '18px' }}>
          <div style={{ fontSize: '10px', color: V.gold, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '6px' }}>Total Growth</div>
          <div style={{ ...mono, fontSize: '20px', color: V.gold, marginBottom: '4px' }}>{fmt(Math.max(0, totalGrowth))}</div>
          <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>+{totalReturnPct}% total return</div>
        </div>
        <div style={{ flex: '1 1 160px', background: 'rgba(100,150,200,0.07)', border: '0.5px solid rgba(100,150,200,0.25)', borderRadius: '10px', padding: '18px' }}>
          <div style={{ fontSize: '10px', color: '#7ab0d4', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '6px' }}>Total Contributed</div>
          <div style={{ ...mono, fontSize: '20px', color: '#7ab0d4', marginBottom: '4px' }}>{monthlyContribution === 0 ? '$0' : fmt(totalContributed)}</div>
          <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>
            {monthlyContribution === 0 ? '$0 additional contributed' : `${fmt(totalContributed)} contributed over ${years} year${years !== 1 ? 's' : ''}`}
          </div>
        </div>
      </div>

      {/* YEAR BY YEAR TABLE */}
      <div style={cCard}>
        <div style={{ ...eyebrow, marginBottom: '12px' }}>Year by Year</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', fontFamily: "'Jost',sans-serif" }}>
            <thead>
              <tr style={{ background: 'rgba(201,168,76,0.1)' }}>
                {['Year', 'Starting', 'Growth', 'Ending Balance'].map(h => (
                  <th key={h} style={{ textAlign: 'left', color: V.gold, fontWeight: 500, padding: '8px 10px', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '10px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {yearlyData.map((row, i) => {
                const isLast = i === yearlyData.length - 1;
                return (
                  <tr key={row.year} style={{ background: isLast ? 'rgba(201,168,76,0.08)' : i % 2 === 1 ? 'rgba(255,255,255,0.02)' : 'transparent', borderTop: '0.5px solid rgba(201,168,76,0.07)' }}>
                    <td style={{ padding: '7px 10px', color: isLast ? V.gold : V.muted, fontWeight: isLast ? 600 : 400 }}>{row.year}</td>
                    <td style={{ padding: '7px 10px', ...mono, color: isLast ? V.gold : V.text }}>{fmt(row.start)}</td>
                    <td style={{ padding: '7px 10px', ...mono, color: V.sage }}>+{fmt(row.growth)}</td>
                    <td style={{ padding: '7px 10px', ...mono, color: isLast ? V.gold : V.text, fontWeight: isLast ? 600 : 400 }}>{fmt(row.end)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOTIVATIONAL NOTE */}
      <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.85 }}>
        {annualReturn === 0 ? (
          <>
            At 0% your balance stays flat.<br />
            This is what premium selling changes — consistent returns that compound over time.
          </>
        ) : (
          <>
            This is what consistent premium selling builds.<br />
            Not one lucky trade. Not speculation.<br />
            Quiet, consistent income — compounded over time.<br /><br />
            ${startingBalance.toLocaleString()} growing at {annualReturn}% becomes {fmt(finalBalance)} in {years} year{years !== 1 ? 's' : ''}.<br /><br />
            The math works. The discipline is the variable.
          </>
        )}
      </p>
    </div>
  );
}

// ─── LOCKED CALCULATOR PLACEHOLDER ──────────────────────────────
const LOCKED_CALCS = {
  calc_retirement: { title: 'Retirement Income Planner', desc: 'Model your complete retirement income picture across multiple streams.', preview: 'Enter trading income, Social Security, pension, rental income, and investments. Set your tax rate, inflation assumptions, and withdrawal strategy. See exactly when you can retire and what your monthly income will look like.' },
  calc_budget:     { title: 'Budget & Cash Flow Planner', desc: 'Map every dollar coming in and going out.', preview: 'Multiple income sources, expense categories, savings rate tracking, and a clear picture of your monthly cash flow. Includes trading income as a separate income stream.' },
  calc_rentvbuy:   { title: 'Rent vs Buy Calculator', desc: 'The true cost comparison.', preview: 'Factor in down payment, mortgage, taxes, insurance, maintenance, opportunity cost of capital, and projected appreciation. See the real break-even point.' },
  calc_debt:       { title: 'Debt Payoff Planner', desc: 'Avalanche vs snowball — which saves you more?', preview: 'Enter all debts with balances, interest rates, and minimum payments. Compare avalanche (highest rate first) vs snowball (smallest balance first) strategies. See exact payoff dates.' },
  calc_college:    { title: 'College Savings Calculator', desc: 'Plan for education costs.', preview: 'Enter child\'s age, target school cost, current savings, and monthly contribution. See projected 529 growth and whether you\'re on track.' },
  calc_networth:   { title: 'Net Worth Tracker', desc: 'Assets minus liabilities — your real financial picture.', preview: 'Track all assets (home, accounts, investments, vehicles) and liabilities (mortgage, loans, credit cards). Watch your net worth grow as you trade.' },
  calc_savings:    { title: 'Savings Goal Calculator', desc: 'How long to reach any target?', preview: 'Enter your goal amount, current savings, and monthly contribution. See exactly when you\'ll reach it and how trading income accelerates the timeline.' },
  calc_car:        { title: 'Car Payment vs Invest Calculator', desc: 'What does that car really cost?', preview: 'Compare buying a car with payments vs investing that same monthly amount. The opportunity cost of depreciating assets is one of the most powerful lessons in personal finance.' },
  calc_emergency:  { title: 'Emergency Fund Calculator', desc: 'How much buffer do you actually need?', preview: 'Calculate your ideal emergency fund based on monthly expenses, job stability, and risk tolerance. See how trading income changes the equation.' },
};

function LockedCalcView({ viewKey, onBack }) {
  const [showPreview, setShowPreview] = useState(false);
  const data = LOCKED_CALCS[viewKey] || { title: 'Coming Soon', desc: '', preview: '' };
  return (
    <div style={{ padding: '28px 24px', maxWidth: '560px' }}>
      <button style={{ ...btnGhost, marginBottom: '24px', fontSize: '12px' }} onClick={onBack}>← Calculators</button>
      <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '28px 24px', position: 'relative' }}>
        <div style={{ position: 'absolute', top: '20px', right: '20px', fontSize: '20px', color: V.gold }}>🔒</div>
        <div style={{ ...eyebrow, marginBottom: '10px' }}>Advanced Calculator</div>
        <h2 style={{ ...serif, fontSize: '26px', fontWeight: 500, color: V.gold, margin: '0 0 12px', lineHeight: 1.25, paddingRight: '32px' }}>{data.title}</h2>
        <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.7, marginBottom: '20px' }}>{data.desc}</p>
        <div style={{ display: 'inline-block', background: 'rgba(201,168,76,0.08)', border: `0.5px solid ${V.border}`, borderRadius: '20px', padding: '4px 14px', fontSize: '10px', color: V.gold, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", marginBottom: '20px' }}>Unlocks at Graduation</div>
        <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", lineHeight: 1.7, fontStyle: 'italic', marginBottom: '16px' }}>
          Complete the full curriculum and pass the Readiness Test to unlock all advanced financial planning calculators.
        </p>
        <button style={{ ...btnGhost, fontSize: '12px', padding: '8px 16px' }} onClick={() => setShowPreview(v => !v)}>
          {showPreview ? 'Hide preview ▲' : 'See what\'s coming →'}
        </button>
        {showPreview && (
          <div style={{ marginTop: '16px', padding: '14px 16px', background: V.bg3, borderRadius: '8px', fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.75 }}>
            {data.preview}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CALCULATORS LANDING PAGE ────────────────────────────────────
const TRADING_CALCS = [
  { view: 'calc_position',  title: 'Position Size Calculator',  quote: 'Risk comes from not knowing what you are doing.' },
  { view: 'calc_options',   title: 'Options Profit Calculator', quote: 'Make the best trades. Money is secondary.' },
  { view: 'calc_breakeven', title: 'Break Even Calculator',     quote: 'Know your exit before you enter.' },
  { view: 'calc_rr',        title: 'Risk / Reward Calculator',  quote: "It's about making money when you're right." },
  { view: 'calc_compound',  title: 'Compound Growth Calculator', quote: 'Compound interest is the eighth wonder of the world.' },
];

function CalculatorsLandingView({ state, onNavigate, onBack }) {
  const isGraduated = state.isGraduated;
  return (
    <div style={{ padding: '28px 24px', maxWidth: '820px' }}>
      <button style={{ ...btnGhost, marginBottom: '20px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ ...eyebrow, marginBottom: '6px' }}>Tools</div>
      <h2 style={{ ...serif, fontSize: '32px', fontWeight: 500, color: V.gold, margin: '0 0 8px' }}>Calculators</h2>
      <p style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', marginBottom: '32px' }}>Tools to size your trades, measure your risk, and plan your financial future.</p>

      <div style={{ ...eyebrow, color: V.text, marginBottom: '12px' }}>Trading Calculators</div>
      <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", marginBottom: '20px' }}>Available now — use these every time you trade.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', marginBottom: '36px' }}>
        {TRADING_CALCS.map(({ view, title, quote }) => (
          <div key={view} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '20px', cursor: 'pointer', transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.5)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = V.border}
            onClick={() => onNavigate(view)}>
            <div style={{ ...serif, fontSize: '18px', color: V.gold, fontWeight: 500, marginBottom: '8px', lineHeight: 1.25 }}>{title}</div>
            <p style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.6, marginBottom: '16px' }}>"{quote}"</p>
            <button style={{ ...btnGold, padding: '8px 16px', fontSize: '12px' }} onClick={e => { e.stopPropagation(); onNavigate(view); }}>Open Calculator →</button>
          </div>
        ))}
      </div>

      <div style={{ ...eyebrow, color: V.text, marginBottom: '12px' }}>Financial Planning {isGraduated ? '' : '🔒'}</div>
      <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", marginBottom: '20px' }}>{isGraduated ? 'Unlocked — your complete financial life in one place.' : 'Unlocks at graduation — your complete financial life in one place.'}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px' }}>
        {Object.entries(LOCKED_CALCS).map(([view, { title, desc }]) => (
          <div key={view} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '20px', cursor: 'pointer', opacity: isGraduated ? 1 : 0.75 }}
            onClick={() => onNavigate(view)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div style={{ ...serif, fontSize: '17px', color: V.gold, fontWeight: 500, lineHeight: 1.25, flex: 1 }}>{title}</div>
              {!isGraduated && <span style={{ fontSize: '14px', color: V.gold, flexShrink: 0, marginLeft: '8px' }}>🔒</span>}
            </div>
            <p style={{ fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.6, margin: 0 }}>{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── MEDITATIONS VIEW ────────────────────────────────────────────
function MeditationsView({ onBack }) {
  const meditations = [
    { icon: '🌅', title: 'Before the Market Opens',      duration: '5 min', desc: 'Ground yourself before the first trade.' },
    { icon: '🌿', title: 'After a Loss',                  duration: '7 min', desc: 'Process with compassion, not judgment.' },
    { icon: '🛑', title: 'When You Want to Revenge Trade', duration: '3 min', desc: 'Pause before a part of you makes a decision.' },
    { icon: '🌟', title: 'After a Win',                   duration: '5 min', desc: 'Receive your success fully.' },
    { icon: '💫', title: 'Setting Your Why',              duration: '8 min', desc: 'Visualize the life you are building toward.' },
    { icon: '🌙', title: 'Releasing a Bad Trading Day',   duration: '6 min', desc: 'Close the day with intention, not regret.' },
    { icon: '💪', title: 'Building Confidence',           duration: '6 min', desc: 'For the days when imposter syndrome shows up.' },
  ];
  return (
    <div style={{ padding: '40px 32px', maxWidth: '780px', margin: '0 auto' }}>
      <button style={{ ...btnGhost, marginBottom: '28px', fontSize: '12px' }} onClick={onBack}>← Inner Work</button>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ ...serif, fontSize: '32px', fontWeight: 400, color: V.gold, margin: '0 0 10px' }}>Trading Meditations</h1>
        <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>
          A moment of stillness before the market opens.<br />A moment of reflection after it closes.
        </p>
      </div>

      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px 24px', marginBottom: '28px' }}>
        <p style={{ ...serif, fontSize: '16px', color: V.muted, fontStyle: 'italic', lineHeight: 1.7, margin: '0 0 6px' }}>
          "Almost everything will work again if you unplug it for a few minutes — including you."
        </p>
        <div style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>— Anne Lamott</div>
      </div>

      <p style={{ fontSize: '14px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.9, marginBottom: '28px', maxWidth: '680px' }}>
        These meditations are not requirements. They are offerings — a few minutes of support available exactly when you need them most. Before a trade when you notice tension or urgency. After a loss when a part of you wants to react. At the end of a hard day when you need to set it down before you can rest.
        <br /><br />
        Each meditation is read aloud in a calm female voice. You can listen, read along, or simply sit with the words.
        <br /><br />
        Your journal is always waiting at the end of each one — to catch whatever the meditation opens.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px', marginBottom: '24px' }}>
        {meditations.map(({ icon, title, duration, desc }) => (
          <div key={title} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '24px' }}>{icon}</div>
            <div style={{ ...serif, fontSize: '18px', color: V.gold, fontWeight: 400, lineHeight: 1.25 }}>{title}</div>
            <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>{duration}</div>
            <div style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.6, flex: 1 }}>{desc}</div>
            <button style={{ ...btnGold, padding: '8px 18px', fontSize: '12px', marginTop: '6px', alignSelf: 'flex-start' }}>Begin →</button>
          </div>
        ))}
      </div>

      <p style={{ fontSize: '12px', color: V.dim, fontFamily: "'Jost',sans-serif", fontStyle: 'italic', textAlign: 'center' }}>
        Audio meditations read in a calm female voice. Your own voice recordings coming soon.
      </p>
    </div>
  );
}

export { LOCKED_CALCS, DteSimView, CalcHeader, CalcPositionView, CalcOptionsProfitView, CalcBreakevenView, CalcRRView, CalcCompoundView, LockedCalcView, CalculatorsLandingView, MeditationsView }
